import type { ManualIncentiveEntry, AccessoryElement, paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { TaxNames } from '@makemydeal/dr-platform-types';
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import type { RetentionTaxContext } from '../../types/AdditionalSelectors';
import type { RetentionTaxRow, RetentionTaxRegion } from './retentionTaxTypes';

export type CategoryKey = 'incentives' | 'vpp' | 'accessories' | 'taxes';

export interface CategoryConfig {
    key: CategoryKey;
    label: string;
    items: { id: string; displayText: string }[];
    rawItems: unknown[];
}

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const withLabel = (name: string | undefined, value: string): string => (name ? `${name}: ${value}` : value);

export interface BuildCategoriesParams {
    incentives: ManualIncentiveEntry[];
    vpp: DraftDealProduct[];
    accessories: AccessoryElement[];
    taxes: paymentServicesTypes.TaxItem[];
    taxContext: RetentionTaxContext;
}

const getIncentiveSource = (incentive: ManualIncentiveEntry): string => {
    if (incentive.manual) return 'Manual';
    return incentive.conditionCategory || 'Program';
};

const getVppSource = (product: DraftDealProduct): string | undefined => {
    // The VPP slide-out "Provider" column value (e.g. Protective, ZURICH, CNA National).
    // Manually-added products have no provider, so label them "Manual"; otherwise show nothing.
    if (product.productProviderName) return product.productProviderName;
    return product.isManualProduct ? 'Manual' : undefined;
};

const getAccessorySource = (accessory: AccessoryElement): string | undefined => {
    if (accessory.isManualAccessory) return 'Manual';
    return accessory.providerName;
};

type TaxRateRegion = Exclude<RetentionTaxRegion, 'total'>;

export const TAX_RATE_REGIONS: TaxRateRegion[] = ['state', 'county', 'city', 'local'];

const REGION_LABELS: Record<TaxRateRegion, string> = {
    state: 'State',
    county: 'County',
    city: 'City',
    local: 'Local'
};

// Full tax names (no abbreviations). The trailing "Tax" and any category qualifier are appended
// afterwards so names read e.g. "County Advanced Upfront Tax".
const TAX_NAME_LABELS: Record<string, string> = {
    advanced: 'Advanced',
    crt: 'Cap Reduction',
    mut: 'Monthly Use',
    flat: 'Flat'
};

// Standalone tax names: these state/region-specific taxes have complete display names and bypass
// the standard composition logic (no region prefix, no category word, no "Tax" suffix appended).
const STANDALONE_TAX_NAMES: Record<string, string> = {
    az_single_item: 'Single Item Tax',
    sales_use_tax_co: 'Sales Use Tax',
    ct_tradein_fee: 'Trade-In Fee',
    fl_doc_stamp: 'Documentary Stamp Tax',
    fl_discretionary: 'Discretionary County Tax',
    ga_ad_valorem: 'Title Ad Valorem Tax',
    sales_use_tax_il: 'Sales Use Tax',
    mn_excise_tax: 'Excise Tax',
    nj_supp: 'Supplemental Titling Fee',
    or_cat: 'Corporate Activity Tax',
    or_privilege: 'Privilege Tax',
    property_tax: 'Property Tax',
    tn_business: 'Business Tax',
    tn_local_options: 'Local Options Tax',
    tn_single_article: 'Single Article Tax',
    tx_vit: 'Vehicle Inventory Tax',
    va_business_license: 'Business License Tax',
    wa_esc: 'Warranty Tax',
    wa_rta: 'Regional Transit Authority Tax'
};

const getHumanTaxName = (taxName?: string): string => {
    if (!taxName) return '';
    return TAX_NAME_LABELS[taxName.toLowerCase()] ?? taxName;
};

const getStandaloneTaxName = (taxName?: string): string | undefined => {
    if (!taxName) return undefined;
    return STANDALONE_TAX_NAMES[taxName.toLowerCase()];
};

// Splits a standalone display name into its base and trailing suffix ("Tax" or "Fee") so
// the category word can be inserted before the suffix: "Warranty Upfront Tax" not "Warranty Tax Upfront".
const splitStandaloneSuffix = (name: string): [string, string] => {
    if (name.endsWith(' Tax')) return [name.slice(0, -4), 'Tax'];
    return [name.slice(0, -4), 'Fee'];
};

// Resolves the display name base and its trailing suffix for a tax item.
// Standalone taxes split their own suffix; standard taxes use "Tax".
const resolveTaxNameParts = (taxItem: paymentServicesTypes.TaxItem): { displayName: string; suffix: string | undefined } => {
    const standaloneName = getStandaloneTaxName(taxItem.taxName);
    if (standaloneName) {
        const [base, standaloneSuffix] = splitStandaloneSuffix(standaloneName);
        return { displayName: base, suffix: standaloneSuffix };
    }
    return { displayName: getHumanTaxName(taxItem.taxName), suffix: 'Tax' };
};

// The Upfront/Capitalised "root category" only applies to lease deals; for other deal types the
// detail is not present, so it is skipped. Monthly Use taxes already convey it in their name.
const getCategoryWord = (taxItem: paymentServicesTypes.TaxItem, isLease: boolean, capped: boolean, crtCapped: boolean): string => {
    if (!isLease) return '';
    if (taxItem.taxName === TaxNames.MonthlyUseTax) return '';
    if (taxItem.taxName === TaxNames.CapCostReduction) return crtCapped ? 'Capitalised' : 'Upfront';
    return capped ? 'Capitalised' : 'Upfront';
};

// Build "{Region} {TaxName} {Category} {Suffix}", dropping any piece that is not available.
const composeTaxRowName = (...parts: (string | undefined)[]): string => {
    return parts.filter(Boolean).join(' ');
};

const normalizeRate = (rate: number): number => Number(rate.toFixed(5));

/**
 * Explodes each manually overridden tax item into one display row per modified jurisdiction rate
 * (US2035248). Items whose top-level rate changed but no jurisdiction did fall back to a single
 * 'total' row. Each row keeps its source tax item and region so the checked rows can later be
 * recombined into real TaxItem objects.
 */
export const buildTaxRows = (taxItems: paymentServicesTypes.TaxItem[], context: RetentionTaxContext): RetentionTaxRow[] => {
    const { isLease, capped, crtCapped } = context;
    const rows: RetentionTaxRow[] = [];

    taxItems.forEach((taxItem, itemIndex) => {
        const { displayName: taxDisplayName, suffix } = resolveTaxNameParts(taxItem);
        const categoryWord = getCategoryWord(taxItem, isLease, capped, crtCapped);
        const manualChanges = taxItem.manualChanges ?? {};
        const breakdown = (taxItem.taxBreakdown ?? {}) as paymentServicesTypes.TaxItemBreakdown;
        const rowKey = `${itemIndex}-${taxItem.taxName ?? 'tax'}`;

        let hasRegionRateOverride = false;

        TAX_RATE_REGIONS.forEach((region) => {
            const rateKey = `${region}TaxRate` as const;
            if (!manualChanges[rateKey]) return;
            const rate = breakdown[rateKey];
            if (rate == null) return;
            hasRegionRateOverride = true;
            const displayName = composeTaxRowName(REGION_LABELS[region], taxDisplayName, categoryWord, suffix);
            rows.push({
                id: `tax-${rowKey}-${region}`,
                displayText: `${displayName}: ${normalizeRate(rate)}%`,
                taxItem,
                region
            });
        });

        if (!hasRegionRateOverride && manualChanges.taxRate && taxItem.taxRate != null) {
            const displayName = composeTaxRowName(undefined, taxDisplayName, categoryWord, suffix);
            rows.push({
                id: `tax-${rowKey}-total`,
                displayText: `${displayName}: ${normalizeRate(taxItem.taxRate)}%`,
                taxItem,
                region: 'total'
            });
        }
    });

    return rows;
};

export const buildCategories = (params: BuildCategoriesParams): CategoryConfig[] => {
    const { incentives, vpp, accessories, taxes, taxContext } = params;

    const result: CategoryConfig[] = [];

    if (incentives.length > 0) {
        result.push({
            key: 'incentives',
            label: 'Rebates and Incentives',
            items: incentives.map((item, index) => ({
                // Append the list index so duplicate codes never collide (shared React key / checkbox state).
                id: `inc-${item.code ?? 'na'}-${index}`,
                displayText: withLabel(item.name, `${formatCurrency(item.amount)} (${getIncentiveSource(item)})`)
            })),
            rawItems: incentives
        });
    }

    if (vpp.length > 0) {
        result.push({
            key: 'vpp',
            label: 'Vehicle Protection Products',
            items: vpp.map((item, index) => {
                const source = getVppSource(item);
                const priced = `${item.productName || 'Protection Product'}: ${formatCurrency(item.productPrice ?? 0)}`;
                return {
                    id: `vpp-${item.productCode ?? 'na'}-${index}`,
                    displayText: source ? `${priced} (${source})` : priced
                };
            }),
            rawItems: vpp
        });
    }

    if (accessories.length > 0) {
        result.push({
            key: 'accessories',
            label: 'Accessories',
            items: accessories.map((item, index) => {
                const source = getAccessorySource(item);
                const amount = formatCurrency(item.amount);
                return {
                    id: `acc-${item.manualAccessoryId ?? item.code ?? 'na'}-${index}`,
                    displayText: withLabel(item.name, source ? `${amount} (${source})` : amount)
                };
            }),
            rawItems: accessories
        });
    }

    if (taxes.length > 0) {
        const taxRows = buildTaxRows(taxes, taxContext);
        if (taxRows.length > 0) {
            result.push({
                key: 'taxes',
                label: 'Taxes',
                items: taxRows.map((item, index) => ({
                    id: `${item.id}-${index}`,
                    displayText: item.displayText
                })),
                rawItems: taxRows
            });
        }
    }

    return result;
};
